import React, { useState } from 'react';
import {
  EmailIcons,
  EyeIcons,
  FilePlusIcons,
  MapPinIcons,
  PhoneIcons,
  StarIcons,
  UserPenIcons,
} from '../../../icons/icons';
import {
  ContextMenu,
  ContextMenuButton,
} from '../../../ui/components/Common/ContextMenu';
import { MenuItemType } from '../../../ui/components/Common/ContextMenu/ContextMenu';
import Pagination from '../../../ui/components/Common/DataTable/Paginaion';
import { NameEmailAvatar } from '../../../ui/components/Common/NameEmailAvatar';
import { CandidateType } from '../constants/mockData';

interface CandidateTableProps {
  candidates: CandidateType[];
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  getMatchColor: (percentage: number) => string;
  selectedCandidates?: string[];
  onCandidateSelect?: (candidateId: string) => void;
  onSelectAll?: () => void;
  onViewProfile?: (candidate: CandidateType) => void;
  onInvite?: (candidate: CandidateType) => void;
  currentPage: number;
  totalPages: number;
  totalCandidates: number;
  candidatesPerPage: number;
  onPageChange: (page: number) => void;
}

export const CandidateTable: React.FC<CandidateTableProps> = ({
  candidates,
  getMatchColor,
  selectedCandidates = [],
  onCandidateSelect,
  onSelectAll,
  onViewProfile,
  onInvite,
  currentPage,
  totalPages,
  totalCandidates,
  candidatesPerPage,
  onPageChange,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<CandidateType | null>(null);

  const allSelected =
    candidates.length > 0 && selectedCandidates.length === candidates.length;
  const isIndeterminate =
    selectedCandidates.length > 0 &&
    selectedCandidates.length < candidates.length;

  const menuItems: MenuItemType<CandidateType>[] = [
    {
      label: 'View Profile',
      icon: <EyeIcons className='w-4 h-4' />,
      permission: [],
      onClick: (row) => onViewProfile?.(row),
    },
    {
      label: 'Add to Invite List',
      icon: <FilePlusIcons className='w-4 h-4' />,
      permission: [],
      onClick: (row) => onInvite?.(row),
    },
    {
      label: 'View Contact Info',
      icon: <PhoneIcons className='w-4 h-4' />,
      permission: [],
      onClick: () => {},
    },
  ];

  return (
    <div className='flex flex-col w-full min-w-0 border border-slate-200 rounded-lg overflow-hidden bg-white'>
      <div
          className='overflow-auto'
          style={{ maxHeight: 'calc(100vh - 280px)' }}
      >
        <table className='w-full border-collapse text-sm'>
          { /* Header */ }
          <thead className='sticky top-0 z-10 bg-slate-50 border-b border-slate-200'>
            <tr>
              { onCandidateSelect && (
                <th className='w-10 px-3 py-2.5 text-left'>
                  <input
                      type='checkbox'
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isIndeterminate;
                      }}
                      onChange={onSelectAll}
                      className='w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer'
                  />
                </th>
              ) }
              <th className='px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider'>
                Candidate
              </th>
              <th className='px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider'>
                Skills
              </th>
              <th className='px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider'>
                Location
              </th>
              <th className='px-3 py-2.5 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-20'>
                Match
              </th>
              <th className='px-3 py-2.5 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-24'>
                Experience (Yrs)
              </th>
              <th className='px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider'>
                Invite Lists
              </th>

              <th className='w-10 px-2 py-2.5' />
            </tr>
          </thead>

          { /* Body */ }
          <tbody className='divide-y divide-slate-100 bg-white'>
            { candidates.map((candidate) => {
              const isSelected = selectedCandidates.includes(candidate.id);
              return (
                <tr
                    key={candidate.id}
                    onClick={() => onViewProfile?.(candidate)}
                    className={`transition-colors cursor-pointer ${
                      isSelected ? 'bg-indigo-50/60' : 'hover:bg-slate-50/70'
                    }`}
                >
                  { /* Checkbox */ }
                  { onCandidateSelect && (
                    <td
                        className='w-10 px-3 py-2'
                        onClick={(e) => {
                          e.stopPropagation();
                          onCandidateSelect(candidate.id);
                        }}
                    >
                      <input
                          type='checkbox'
                          checked={isSelected}
                          onChange={() => {}}
                          className='w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer'
                      />
                    </td>
                  ) }

                  { /* Candidate */ }
                  <td className='px-3 py-2 min-w-[200px]'>
                    <div className='flex items-center gap-1'>
                      <NameEmailAvatar
                          name={candidate.name}
                          email={candidate.email}
                          avatar={candidate.avatar}
                          avatarWidth='32px'
                          avatarHeight='32px'
                          nameFontSize='13px'
                          emailFontSize='11px'
                      />
                      <span title='AI Verified'>
                        <StarIcons className='w-3 h-3 text-purple-400 shrink-0 ml-1' />
                      </span>
                    </div>
                  </td>

                  { /* Skills */ }
                  <td className='px-3 py-2 min-w-[160px]'>
                    <div className='flex flex-wrap gap-1'>
                      { candidate.skills.slice(0, 4).map((skill, i) => (
                        <span
                            key={i}
                            className='px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[11px] font-medium border border-slate-200/70'
                        >
                          { skill }
                        </span>
                      )) }
                      { candidate.skills.length > 2 && (
                        <span className='text-[11px] text-slate-400 font-medium self-center'>
                          +{ candidate.skills.length - 2 } more
                        </span>
                      ) }
                    </div>
                  </td>

                  { /* Location */ }
                  <td className='px-3 py-2 min-w-[140px]'>
                    { candidate.location ? (
                      <div className='flex items-center gap-1 text-xs text-slate-500'>
                        <MapPinIcons className='w-3 h-3 shrink-0' />
                        <span className='truncate max-w-[120px]'>
                          { candidate.location }
                        </span>
                      </div>
                    ) : (
                      <span className='text-xs text-slate-300'>—</span>
                    ) }
                  </td>

                  { /* Match */ }
                  <td className='px-3 py-2 w-20 text-center'>
                    <span
                        className={`inline-flex items-center justify-center w-12 h-6 rounded text-white font-bold text-[11px] ${getMatchColor(
                          Math.round(candidate.score * 100),
                        )}`}
                    >
                      { Math.round(candidate.score * 100) }%
                    </span>
                  </td>

                  { /* Experience */ }
                  <td className='px-3 py-2 w-36 text-center'>
                    <span className='text-xs text-slate-600 font-medium'>
                      { Math.floor(candidate.experience) }
                    </span>
                  </td>

                  { /* Invite Lists */ }
                  <td className='px-3 py-2 min-w-[140px]'>
                    { candidate.inviteLists &&
                    candidate.inviteLists.length > 0 ? (
                      <div className='flex flex-wrap gap-1'>
                          { candidate.inviteLists.slice(0, 2).map((list, i) => (
                          <span
                                key={i}
                                className='px-1.5 py-0.5 text-[10px] font-bold uppercase rounded border bg-emerald-50 text-emerald-600 border-emerald-100 tracking-tight'
                            >
                              { list }
                            </span>
                          )) }
                          { candidate.inviteLists.length > 2 && (
                        <span className='text-[11px] text-slate-400 self-center'>
                              +{ candidate.inviteLists.length - 2 }
                            </span>
                          ) }
                        </div>
                      ) : (
                        <span className='text-xs text-slate-300'>—</span>
                      ) }
                  </td>

                  { /* Context Menu */ }
                  <td
                      className='w-10 px-2 py-2'
                      onClick={(e) => e.stopPropagation()}
                  >
                    <ContextMenuButton
                        row={candidate}
                        onOpenMenu={(e, row) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuRow(row as CandidateType);
                        }}
                        onCloseMenu={() => {
                          setMenuAnchor(null);
                          setMenuRow(null);
                        }}
                        anchorEl={menuAnchor}
                    />
                  </td>
                </tr>
              );
            }) }
          </tbody>
        </table>
      </div>

      { menuAnchor && menuRow && (
        <ContextMenu
            anchorEl={menuAnchor}
            onClose={() => {
              setMenuAnchor(null);
              setMenuRow(null);
            }}
            row={menuRow}
            menuItems={menuItems}
        />
      ) }

      { /* Pagination */ }
      <div className='border-t border-slate-200 bg-white'>
        <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalCandidates}
            rowsPerPage={candidatesPerPage}
            handlePageChange={onPageChange}
        />
      </div>
    </div>
  );
};
